import { HttpClient } from "@angular/common/http"
import { Injectable, isDevMode } from "@angular/core"
import { ActivatedRoute, Params } from "@angular/router"
import { NotificationsService } from "angular2-notifications"
import {
    Observable,
    of as observableOf,
    ReplaySubject,
    throwError as observableThrowError,
} from "rxjs"
import { catchError } from "rxjs/operators"
import Utils from "../utils/utils"
import { CookiesService } from "./cookies.service"
import { LmApiService } from "./lm-api.service"
import { StudentSearchFeatureWell } from "../models/student-site/student-search-featurewell.model"
import { JoinedRoster } from "../models/student-site/joined-roster.model"

declare var window: any

@Injectable()
export class UserService {
    public readonly httpHeaders = {
        "Content-Type": "application/x-www-form-urlencoded",
    }
    public favoritesReady = new ReplaySubject<any>()
    public studentHomepage = new ReplaySubject<any>()
    public userAccountEditUrl = window.PBSLM.NG.PBS_ACCOUNTS_PROFILE_EDIT
    public shouldShowVirginiaModal = window.PBSLM.SHOW_VIRGINIA_MODAL
    public studentAlertData = window.PBSLM.NG.STUDENT_ALERT_BAR_DATA
    public teacherAlertData = window.PBSLM.NG.TEACHER_ALERT_BAR_DATA
    private _loggedIn = window.PBSLM.NG.IS_AUTHENTICATED
    private _adminDisplay = window.PBSLM.NG.ADMIN_DISPLAY
    private _pbsAuthUrl = window.PBSLM.NG.PBS_AUTH_URL
    private _pbsAuthRegisterUrl = window.PBSLM.NG.PBS_AUTH_REGISTER_URL
    private _googleAuthUrl = window.PBSLM.NG.GOOGLE_AUTH_URL
    private _facebookAuthUrl = window.PBSLM.NG.FACEBOOK_AUTH_URL
    private _logoutUrl = window.PBSLM.NG.LOGOUT_URL
    private _studentAuthUrl = window.PBSLM.NG.STUDENT_AUTH_URL
    private _firstName = window.PBSLM.NG.FIRST_NAME
    private _userName = window.PBSLM.NG.USER_NAME
    private _isStudent = window.PBSLM.NG.IS_STUDENT // true if you are logged in as a student
    private _hasStudentExperience = false
    private _userPK = window.PBSLM.NG.USER_PK
    private _isYoungStudent = window.PBSLM.NG.IS_YOUNG_STUDENT
    private _isSU = window.PBSLM.NG.IS_SU
    private _isSUActiveUser = window.PBSLM.NG.IS_SU_ACTIVE_USER
    private _preloadedEndpoints = window.PBSLM.PRELOADED_ENDPOINTS
    private _favorites = []
    private executedQueryParams: string[] = []

    constructor(
        private http: HttpClient,
        private lmApi: LmApiService,
        private cookieService: CookiesService,
        private notificationService: NotificationsService
    ) {
        this.getFavorites()
    }

    public static getFavorite(loId: string, favorites) {
        for (const favorite of favorites) {
            if (favorite.loId === loId) {
                return favorite
            }
        }
        return null
    }

    public hasStudentExperience() {
        return this._hasStudentExperience || this._isStudent
    }

    public setStudentExperience(isStudentExperience: boolean = true) {
        this._hasStudentExperience = this.isStudent() || isStudentExperience
        this.lmApi.studentExperience = this._hasStudentExperience
    }

    public hasAdminDisplay() {
        return this._adminDisplay || this._isSU
    }

    public isAdmin() {
        return this._adminDisplay
    }

    public isUserImpersonated() {
        return this._isSU
    }

    public isImpersonatedUserActive() {
        return this._isSUActiveUser
    }

    public isLoggedIn() {
        return this._loggedIn
    }

    public getPbsAuthUrl(nextURL, register: boolean = false) {
        if (register) {
            return this._pbsAuthRegisterUrl + this.nextUrlBuilder(nextURL)
        }
        return this._pbsAuthUrl + this.nextUrlBuilder(nextURL)
    }

    public getGoogleAuthUrl(nextURL, isStudent = false) {
        //nextURL additional check was added in order to keep filters/url params after login redirect
        let url = this._googleAuthUrl + this.nextUrlBuilder(nextURL)
        //todo: investigate BE validation on type=student
        isStudent && (url += "&type=student")
        return url
    }

    public getFacebookAuthUrl(nextURL) {
        return this._facebookAuthUrl + this.nextUrlBuilder(nextURL)
    }

    public nextUrlBuilder(nextURL): string {
        return (
            "?next=" +
            encodeURIComponent(this.safeNextURL(nextURL ? nextURL : window.location.href))
        )
    }

    public switchUserExperience(isStudent: boolean, target: string) {
        //in this function window.location is used instead of router.navigate because router navigate
        //is not triggering onActive() router guard function
        let parsedURL = new URL(window.location.href)
        if (isStudent && parsedURL.pathname !== "/") {
            parsedURL.searchParams.set("student", "true")
        } else if (isStudent && parsedURL.pathname === "/") {
            parsedURL.pathname = "/student/"
            parsedURL.search = ""
        } else if (
            (!isStudent && parsedURL.pathname === "/student/") ||
            parsedURL.pathname.startsWith("/student/")
        ) {
            parsedURL.pathname = "/"
            parsedURL.search = ""
        } else {
            parsedURL.searchParams.delete("student")
        }
        window.open(parsedURL.toString(), target)
    }

    public getLogoutUrl() {
        return this._logoutUrl
    }

    public getStudentAuthUrl() {
        return this._studentAuthUrl
    }

    public getFirstName() {
        return this._firstName
    }

    public getUserName() {
        return this._userName
    }

    public isStudent() {
        return this._isStudent
    }

    public getUserPK() {
        return this._userPK
    }

    public getPreloadedEndpoints(): any {
        return this._preloadedEndpoints
    }

    public getWithPreload(url) {
        if (this.getPreloadedEndpoints().hasOwnProperty(url)) {
            isDevMode() && console.log("Using pre-fetched API:", url)
            return observableOf(this.getPreloadedEndpoints()[url])
        }
        isDevMode() &&
            console.debug(
                "No pre-fetched for:",
                url,
                ", available:",
                Object.keys(this.getPreloadedEndpoints())
            )
        return this.lmApi
            .getWithCookiesInParams(url)
            .pipe(catchError((error) => observableThrowError(error)))
    }

    public addFavorite(learningObjectId: string) {
        return this.lmApi
            .post(`/api/v2/favorite/`, { lo_id: learningObjectId })
            .pipe(catchError((error) => observableThrowError(error)))
            .subscribe((response: any) => {
                this._favorites.push({
                    loId: learningObjectId,
                    favoriteId: response.id,
                })
                this.favoritesReady.next(this._favorites)
            })
    }

    public deleteFavorite(favoriteId: number) {
        return this.lmApi
            .delete(`/api/v2/favorite/${favoriteId}/`)
            .pipe(catchError((error) => observableThrowError(error)))
            .subscribe(() => {
                this._favorites = this._favorites.filter((favorite) => {
                    return favorite.favoriteId !== favoriteId
                })
                this.favoritesReady.next(this._favorites)
            })
    }

    public getFavorites() {
        if (this.isLoggedIn()) {
            return this.lmApi
                .get("/api/v2/favorites/")
                .pipe(catchError((error) => observableThrowError(error)))
                .subscribe((favorites: any) => {
                    favorites.objects.forEach((favorite) => {
                        this._favorites.push({
                            loId: favorite.lo_id,
                            favoriteId: favorite.id,
                        })
                    })
                    this.favoritesReady.next(this._favorites)
                })
        } else {
            this.favoritesReady.next([])
        }
    }

    // INFO: First step of student login, the user provides his username.
    public postStudentUsername(username: string): Observable<any> {
        const apiUrl: string = "/student/get-secret-question/"
        const payload: string = "username=" + username

        return this.lmApi
            .post(apiUrl, payload, { headers: this.httpHeaders })
            .pipe(catchError((error) => observableThrowError(error)))
    }

    // INFO: Second step of student login, the user provides the answer to his secret question.
    public postSecretAnswer(
        username: string,
        secretQuestion: string,
        secretAnswer: string,
        nextUrl: string
    ): Observable<any> {
        const apiUrl: string = "/student/validate-login/"
        const payload: string =
            "username=" +
            username +
            "&secure_secret_question=" +
            secretQuestion +
            "&secure_secret_answer=" +
            secretAnswer +
            `&next_url=${encodeURIComponent(nextUrl)}`
        return this.lmApi
            .post(apiUrl, payload, { headers: this.httpHeaders })
            .pipe(catchError((error) => observableThrowError(error)))
    }

    public postCreateStudent(data): Observable<any> {
        const apiUrl: string = "/api/v2/student/"
        return this.lmApi
            .post(apiUrl, data)
            .pipe(catchError((error) => observableThrowError(error)))
    }

    public isYoungStudent() {
        return this._isYoungStudent
    }

    public validateCode(code) {
        return this.lmApi
            .post("/student/assignment-validation/", { code })
            .pipe(catchError((error) => observableThrowError(error)))
    }

    public updateResult(resultID, payload) {
        return this.lmApi
            .put(`/tools/api/v2/result/${resultID}/`, payload)
            .pipe(catchError((error) => observableThrowError(error)))
    }

    public fetchClassDetails(code) {
        return this.lmApi
            .get(`/tools/rosters/api/v2/rosters/rostercode/${code}/`)
            .pipe(catchError((error) => observableThrowError(error)))
    }

    public joinClass(rosterId, studentData) {
        return this.lmApi
            .post(`/tools/rosters/api/v2/rosters/${rosterId}/students/`, studentData)
            .pipe(catchError((error) => observableThrowError(error)))
    }

    public leaveClass(joinedRoster: JoinedRoster) {
        return this.lmApi
            .delete(joinedRoster.leaveRosterURL)
            .pipe(catchError((error) => observableThrowError(error)))
    }

    public enterStudentCode(studentCode) {
        return this.lmApi
            .post("/student/dispatch_code/", { code: studentCode })
            .pipe(catchError((error) => observableThrowError(error)))
    }

    public ifPreviewOn() {
        return !!this.cookieService.getCookie("pmc")
    }

    public runOnceForQueryParam(route: ActivatedRoute, queryParam: string, callback: any) {
        return route.queryParams.subscribe((params: Params) => {
            if (params[queryParam] !== undefined) {
                if (this.executedQueryParams.indexOf(queryParam) === -1) {
                    this.executedQueryParams.push(queryParam)
                    callback(params[queryParam])
                }
            }
        })
    }

    public getStudentQueryParams() {
        if (this.hasStudentExperience()) {
            return { student: true }
        }
        return {}
    }

    public addStudentInUrl(url) {
        if (this.hasStudentExperience()) {
            return url + (url.includes("?") ? "&student=true" : "/?student=true")
        }
        return url
    }

    private safeNextURL(nextURL) {
        if (nextURL) {
            return nextURL
        }
        return window.location.pathname
    }
}
